/**
 * Resolves the selected option values to a variant and announces it with a
 * bubbling `variant:changed` event — `{ variant, pendingOption,
 * productLowStock }`, where `pendingOption` names the first option still left
 * unchosen and `productLowStock` is the product's total when it's low.
 */
class ProductFormOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", this._onChange);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this._onChange);
  }

  get inputs() {
    return [...this.querySelectorAll("[data-option-index]")];
  }

  get selects() {
    return [...this.querySelectorAll("select[data-option-index]")];
  }

  _onChange = () => {
    // Read on every change rather than cached: a colourway swap morphs in a
    // new product's data under this same element.
    const data = window.scrapeSectionData(this);
    const variants = data.variants || [];
    const selected = this._selectedValues();
    const pendingSelect = this.selects.find((select) => !select.value);

    const variant = pendingSelect
      ? null
      : variants.find((candidate) => this._matches(candidate, selected)) ||
        null;

    this._updateAvailability(variants, selected);
    this.selects.forEach((select) =>
      select.classList.toggle("indeterminate", !select.value),
    );

    this.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          variant,
          pendingOption: pendingSelect?.dataset.optionName || null,
          productLowStock: data.low_stock || 0,
        },
      }),
    );
  };

  /**
   * @returns {Array<string|null>} Selected value per option position; null
   *   where nothing is chosen yet.
   */
  _selectedValues() {
    const selected = [];

    this.inputs.forEach((input) => {
      selected[Number(input.dataset.optionIndex)] = input.value || null;
    });

    return selected;
  }

  _matches(variant, selected, skipIndex = -1) {
    return selected.every(
      (value, index) =>
        index === skipIndex ||
        value === null ||
        variant.options[index] === value,
    );
  }

  // Flags values that are sold out, in store, waitlisted, preorders or low in
  // stock, alongside the other current selections. Only matters when a
  // product has more than one select.
  _updateAvailability(variants, selected) {
    if (this.selects.length < 2) return;

    const findInStore = this.closest("product-form")?.findInStore;

    this.selects.forEach((select) => {
      const index = Number(select.dataset.optionIndex);

      [...select.options].forEach((option) => {
        if (!option.value) return;

        const candidates = variants.filter(
          (variant) =>
            variant.options[index] === option.value &&
            this._matches(variant, selected, index),
        );
        const available = candidates.filter((variant) => variant.available);

        // Low stock only applies once the value narrows to a single variant
        const lowStock =
          candidates.length === 1 && candidates[0].available
            ? candidates[0].low_stock
            : 0;

        let key = null;
        if (!available.length) {
          // Find In-Store wins over the waitlist, as in product-form.
          if (
            candidates.some((variant) => findInStore?.hasStoreStock(variant.id))
          ) {
            key = "localeOptionFindInStore";
          } else if (candidates.some((variant) => variant.waitlist)) {
            key = "localeOptionWaitlist";
          } else {
            key = "localeOptionSoldOut";
          }
        } else if (available.every((variant) => variant.preorder)) {
          key = "localeOptionPreorder";
        } else if (lowStock) {
          key = "localeOptionLowStock";
        }

        option.textContent = key
          ? window.theme.translations(this.dataset, key, {
              value: option.value,
              quantity: lowStock,
            })
          : option.value;
      });
    });
  }
}

if (!customElements.get("product-form-options")) {
  customElements.define("product-form-options", ProductFormOptions);
}
